export const PREFER_ORIG_ID_OUTPUT_REFERENCES_KEY = Symbol(
  "prefer-orig-id-output-references"
);

function findReferenceByOrigId(
  list: any[] | undefined,
  origId?: string | null
) {
  if (!origId || !Array.isArray(list)) return null;

  return (
    list.find((item: any) => String(item?.origId) === String(origId)) || null
  );
}

function findReferenceByIndex(list: any[] | undefined, index: number | null) {
  if (index === null || !Array.isArray(list)) return null;

  return (
    list.find(
      (item: any) => typeof item?.index === "number" && item.index === index
    ) || null
  );
}

function normalizePositiveNumber(value: unknown): number | undefined {
  return typeof value === "number" &&
    Number.isFinite(value) &&
    Number.isInteger(value) &&
    value > 0
    ? value
    : undefined;
}

function resolveDisplayStepNumber({
  matched,
  matchedIndex,
  parsedIndex,
  preferOrigId,
}: {
  matched: any;
  matchedIndex: number | null;
  parsedIndex: number | null;
  preferOrigId: boolean;
}): number | undefined {
  const matchedStepNumber = normalizePositiveNumber(matched?.stepNumber);
  if (matchedStepNumber !== undefined) return matchedStepNumber;

  if (!preferOrigId) return undefined;

  return (
    normalizePositiveNumber(matchedIndex) ??
    normalizePositiveNumber(parsedIndex)
  );
}

export function parseOutputIndexFromName(name: string): number | null {
  const m = name.match(/^\/(\d+)\/#/);
  if (!m || !m[1]) return null;

  const index = parseInt(m[1], 10);
  return Number.isNaN(index) ? null : index;
}

export function parseMetaoutputIndexFromName(name: string): number | null {
  const m = name.match(/#ix-(\d+)/);
  if (!m || !m[1]) return null;

  const index = parseInt(m[1], 10);
  return Number.isNaN(index) ? null : index;
}

export function rewriteOutputNameIndex(name: string, newIndex: number): string {
  return name.replace(/^\/\d+/, `/${newIndex}`);
}

export function rewriteMetaoutputNameIndex(
  name: string,
  newIndex: number
): string {
  return name.replace(/#ix-\d+/, `#ix-${newIndex}`);
}

export function normalizeOutputReference({
  name,
  origId,
  outputList,
  preferOrigId = true,
}: {
  name: string;
  origId?: string | null;
  outputList?: any[];
  preferOrigId?: boolean;
}): {
  name: string;
  index: number | null;
  stepNumber?: number;
  origId?: string | null;
} {
  const parsedIndex = parseOutputIndexFromName(name);
  const matched = preferOrigId
    ? findReferenceByOrigId(outputList, origId)
    : findReferenceByIndex(outputList, parsedIndex);
  const matchedIndex =
    preferOrigId && matched && typeof matched.index === "number"
      ? matched.index
      : null;

  return {
    name:
      matchedIndex !== null ? rewriteOutputNameIndex(name, matchedIndex) : name,
    index: matchedIndex ?? parsedIndex,
    stepNumber: resolveDisplayStepNumber({
      matched,
      matchedIndex,
      parsedIndex,
      preferOrigId,
    }),
    origId:
      matched?.origId !== undefined && matched?.origId !== null
        ? String(matched.origId)
        : origId,
  };
}

export function normalizeMetaoutputReference({
  name,
  origId,
  metaoutputList,
  preferOrigId = true,
}: {
  name: string;
  origId?: string | null;
  metaoutputList?: any[];
  preferOrigId?: boolean;
}): {
  name: string;
  index: number | null;
  stepNumber?: number;
  origId?: string | null;
} {
  const parsedIndex = parseMetaoutputIndexFromName(name);
  const matched = preferOrigId
    ? findReferenceByOrigId(metaoutputList, origId)
    : findReferenceByIndex(metaoutputList, parsedIndex);
  const matchedIndex =
    preferOrigId && matched && typeof matched.index === "number"
      ? matched.index
      : null;

  return {
    name:
      matchedIndex !== null
        ? rewriteMetaoutputNameIndex(name, matchedIndex)
        : name,
    index: matchedIndex ?? parsedIndex,
    stepNumber: resolveDisplayStepNumber({
      matched,
      matchedIndex,
      parsedIndex,
      preferOrigId,
    }),
    origId:
      matched?.origId !== undefined && matched?.origId !== null
        ? String(matched.origId)
        : origId,
  };
}
